'use client'

import { useSearchParams, useRouter } from 'next/navigation'
import { useCallback, useEffect, useRef, useState } from 'react'

import {
  useAddressByCoordQuery,
  useSearchPlaceListQuery,
  getCurrentLocation,
  DEFAULT_PLACE_SEARCH_MAX_DISTANCE,
  DEFAULT_PLACE_SEARCH_PAGE,
  DEFAULT_PLACE_SEARCH_SIZE,
  PLACE_SEARCH_SORT,
  useCurrentLocationStore,
} from '@/entities/place'
import { logEvent } from '@/shared/external/firebase/analytics'
import { DEFAULT_CURRENT_LOCATION } from '@/shared/lib'
import { EVENT_NAMES, LABELS } from '@/shared/lib/analytics'
import { useSnackbarStore } from '@/shared/model'

const useSearchPlace = () => {
  const searchParams = useSearchParams()
  const initialKeyword = searchParams.get('keyword') || ''
  const [keyword, setKeyword] = useState(initialKeyword)
  const [inputValue, setInputValue] = useState(initialKeyword)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const router = useRouter()

  // router query로 bottomsheet 상태 관리 (OS 뒤로가기 대응)
  const isAddressBottomSheetOpen = searchParams.get('address') === 'open'
  // 로깅된 장소들을 추적하기 위한 ref
  const loggedPlaceIds = useRef<Set<number>>(new Set())

  const { setCurrentLocationState, setAddressName, get } =
    useCurrentLocationStore()
  const latitude = get('latitude')
  const longitude = get('longitude')
  const addressName = get('addressName')

  const {
    actions: { showSnackbar },
  } = useSnackbarStore()

  const fetchCurrentLocation = useCallback(async () => {
    const currentLocation = await getCurrentLocation()
    setCurrentLocationState({
      latitude: currentLocation.latitude,
      longitude: currentLocation.longitude,
    })
  }, [setCurrentLocationState])

  useEffect(() => {
    if (
      latitude !== DEFAULT_CURRENT_LOCATION.LATITUDE &&
      longitude !== DEFAULT_CURRENT_LOCATION.LONGITUDE
    ) {
      return
    }
    fetchCurrentLocation()
  }, [fetchCurrentLocation, latitude, longitude])

  const {
    data: placeListData,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    isError,
    isLoading,
    refetch: retrySearchPlace,
  } = useSearchPlaceListQuery({
    q: keyword,
    lng: longitude,
    lat: latitude,
    size: DEFAULT_PLACE_SEARCH_SIZE,
    maxDistance: DEFAULT_PLACE_SEARCH_MAX_DISTANCE,
    page: DEFAULT_PLACE_SEARCH_PAGE,
    sort: PLACE_SEARCH_SORT.DISTANCE,
  })

  useEffect(() => {
    if (placeListData?.content) {
      placeListData.content.forEach(place => {
        // 이미 로깅된 장소인지 확인
        if (!loggedPlaceIds.current.has(place.serverId)) {
          logEvent(EVENT_NAMES.WEB_VIEW.SEO.SHOW.SEARCH_ITEM, {
            label: LABELS.WEB_VIEW.SEO.SHOW.PARTNERS_LIST,
            store_name: place.placeName,
            distance: place.distance?.toFixed(2),
            is_trusted: place.trustedPartner,
          })
          // 로깅된 장소 ID를 Set에 추가
          loggedPlaceIds.current.add(place.serverId)
        }
      })
    }
  }, [placeListData])

  // 메타데이터 동적 업데이트
  useEffect(() => {
    if (placeListData?.totalElements && keyword) {
      const resultCount = placeListData?.totalElements
      const newDescription = `"${keyword}"에 대한 ${resultCount}곳의 정비소의 위치와 후기, 예약 및 가격 정보를 알려드릴게요.`

      // meta description 업데이트
      const metaDescription = document.querySelector('meta[name="description"]')
      if (metaDescription) {
        metaDescription.setAttribute('content', newDescription)
      }
    }
  }, [placeListData?.totalElements, keyword])

  const { data: addressByCoordData } = useAddressByCoordQuery({
    longitude: longitude,
    latitude: latitude,
  })

  // addressByCoordData가 변경될 때 address_name만 store에 저장
  useEffect(() => {
    if (addressByCoordData?.documents[0]?.address?.address_name) {
      setAddressName(addressByCoordData.documents[0].address.address_name)
    }
  }, [addressByCoordData, setAddressName])

  // query string 생성
  const createQueryString = useCallback(
    (name: string, value: string) => {
      const params = new URLSearchParams(searchParams.toString())
      params.set(name, value)
      return params.toString()
    },
    [searchParams]
  )

  const handleRetrySearchPlace = useCallback(() => {
    retrySearchPlace()
  }, [retrySearchPlace])

  const handleSearchPlace = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        const nextKeyword = inputRef.current?.value?.trim() ?? ''
        // 2글자 미만인 경우 toast
        if (nextKeyword.length < 2) {
          showSnackbar({ text: '2글자 이상 입력해주세요.' })
          return
        }
        setKeyword(nextKeyword)
        // searchParams 업데이트
        router.push(
          `/place/search?${createQueryString('keyword', nextKeyword)}`
        )
      }
    },
    [router, createQueryString, showSnackbar]
  )

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setInputValue(e.target.value)
    },
    []
  )

  const handleClearInput = () => {
    if (inputRef.current) {
      inputRef.current.value = ''
      inputRef.current.focus()
    }
    setInputValue('')

    logEvent(EVENT_NAMES.WEB_VIEW.SEO.CLICK.SEARCH_ITEM, {
      label: LABELS.WEB_VIEW.SEO.CLICK.SEARCH_KEYWORD_INPUT_RESET,
    })
  }

  const handleOpenAddressBottomSheet = () => {
    // query parameter로 bottomsheet 열기
    router.push(`/place/search?${createQueryString('address', 'open')}`)
    logEvent(EVENT_NAMES.WEB_VIEW.SEO.CLICK.SEARCH_ITEM, {
      label: LABELS.WEB_VIEW.SEO.CLICK.SEARCH_ADDRESS,
    })
    logEvent(EVENT_NAMES.WEB_VIEW.SEO.SHOW.SEARCH_ITEM, {
      label: LABELS.WEB_VIEW.SEO.SHOW.SEARCH_ADDRESS_BOTTOM_SHEET,
    })
  }

  const handleCloseAddressBottomSheet = () => {
    // query parameter에서 address 제거하여 bottomsheet 닫기
    const params = new URLSearchParams(searchParams.toString())
    params.delete('address')
    const newQueryString = params.toString()
    router.push(`/place/search${newQueryString ? `?${newQueryString}` : ''}`)
  }

  return {
    state: {
      inputRef,
      keyword,
      inputValue,
      isFetchingNextPage,
      hasNextPage,
      isError,
      isLoading,
      isAddressBottomSheetOpen,
    },
    data: {
      placeListData,
      latitude: latitude,
      longitude: longitude,
      addressName: addressName,
    },
    actions: {
      fetchNextPage,
      handleSearchPlace,
      handleInputChange,
      handleClearInput,
      handleOpenAddressBottomSheet,
      handleCloseAddressBottomSheet,
      handleRetrySearchPlace,
    },
  }
}

export default useSearchPlace
